import { EnrichmentPlugin, Event } from '@amplitude/analytics-types';
import { useEffect } from 'react';

import { AmplitudeAutoTrackEvents } from '@/components/Analytics/amplitude.types';
import { MY_ITEMS_HUB_MIN_WIDTH } from '@/pages/MyItems/components/MyItemsShell/useIsMyItemsHubEligible';
import { TrackingEvents } from '@/utils/amplitude/analytics/core.types';
import { AmplitudeExperiments, AmplitudeVariants } from '@/utils/amplitude/experiments/AmplitudeExperiments';
import { AmplitudePageName, getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';

const MY_ITEMS_HUB_PAGE_NAMES = [AmplitudePageName.MyItems, AmplitudePageName.MyBids, AmplitudePageName.WonItems];
const AMP_EVENTS: string[] = [
    TrackingEvents.AuctionMarketingProgram.AmpSeen,
    TrackingEvents.AuctionMarketingProgram.AmpClicked,
];

export enum MyItemsHubSource {
    Hub = 'My Items Hub',
    Legacy = 'Legacy Surface',
}

/**
 * Adds `Source` to auto-tracked page views and to AMP Seen/Clicked (leaderboard ad) on Favorites, My Bids and
 * Won Items so both LAAC-2204 experiment arms are comparable on page views and ad performance.
 */
export class MyItemsHubPagePropertiesPlugin implements EnrichmentPlugin {
    name = 'my-items-hub-page-properties-plugin';
    readonly type = 'enrichment';
    isHubShown = false;

    execute = async (event: Event) => {
        const props = event.event_properties;
        const autoTrackUrl = props?.['[Amplitude] Page URL'];
        const isHubPageView =
            Boolean(autoTrackUrl) &&
            event.event_type === AmplitudeAutoTrackEvents.PageViewed &&
            MY_ITEMS_HUB_PAGE_NAMES.includes(getAmplitudePageNameFromPath(autoTrackUrl));
        // AMP events carry the page they fired on, so late (post ad fetch) events are still attributed correctly
        const isHubAmpEvent =
            AMP_EVENTS.includes(event.event_type) && MY_ITEMS_HUB_PAGE_NAMES.includes(props?.['Page Name']);
        if (props && (isHubPageView || isHubAmpEvent)) {
            props['Source'] = this.isHubShown ? MyItemsHubSource.Hub : MyItemsHubSource.Legacy;
        }
        return event;
    };
}

/**
 * Single instance, registered before amplitude.init (initializeAmplitudeSDK.client) so the landing page view is
 * enriched too; React only keeps `isHubShown` current afterwards.
 */
export const myItemsHubPagePropertiesPlugin = new MyItemsHubPagePropertiesPlugin();

/** Best boot-time guess (before React mounts) of whether the hub renders: SSR variant, real width, logged in. */
export const isMyItemsHubShownAtBoot = (
    variants: Record<string, { value?: string } | undefined> | undefined,
    userId: string | null | undefined
) =>
    variants?.[AmplitudeExperiments.MyItemsShellPersistentLeftNavAndShell]?.value ===
        AmplitudeVariants.MyItemsShellPersistentLeftNavAndShell.Treatment &&
    window.innerWidth > MY_ITEMS_HUB_MIN_WIDTH &&
    Boolean(userId);

/** <em>Only use inside AmplitudeProvider.</em> */
export const useMyItemsHubPagePropertiesPlugin = (isHubShown: boolean) => {
    useEffect(() => {
        myItemsHubPagePropertiesPlugin.isHubShown = isHubShown;
    }, [isHubShown]);
};
